import { useCallback, useEffect, useMemo } from "react";
import { useRouter } from "@tanstack/react-router";
import { useAuth } from "../context/AuthContext";
import { useScope } from "../context/ScopeContext";
import { commentNotificationsReadEnabled } from "../utils/commentNotificationsReadEnabled";
import { resolveScopedChatbotIds } from "../utils/scopedChatbotIds";
import { showActionToast } from "../utils/notify";
import { translationNavigation } from "../utils/translations";
import { useDashboardTranslate } from "../hooks/useDashboardTranslate";
import { useConversationUnreadCounts } from "../hooks/useConversationUnreadCounts";
import {
  normalizeUnreadCounts,
  parseMentionCursor,
  resolveMentionToast,
} from "./reportCommentNotificationLogic";
import type { MentionNotificationTarget } from "./reportCommentNotificationLogic";

const MENTION_TOAST_CURSOR_STORAGE_PREFIX = "di_mention_toast_last_seen_comment_id";

function readMentionCursor(storageKey: string): number | null {
  try {
    return parseMentionCursor(window.localStorage.getItem(storageKey));
  } catch {
    return null;
  }
}

function writeMentionCursor(storageKey: string, commentId: number): void {
  try {
    window.localStorage.setItem(storageKey, String(commentId));
  } catch {
    // Ignore storage failures — worst case the toast repeats after a reload.
  }
}

function navigateToReportComment(history: { push: (href: string) => void }, search: string): void {
  history.push(`/${search}`);
}

export default function ReportCommentNotifications() {
  const { history } = useRouter();
  const auth = useAuth() || {};
  const scope = useScope() || {};
  const { chatbotIds: scopeChatbotIds = [] } = scope;
  const isAuthenticated = auth.isAuthenticated === true;
  const hasOrganizationContext = auth.hasOrganizationContext === true;
  const userKey = auth.user?.sub || "local";
  const t = useDashboardTranslate(translationNavigation);

  const cursorStorageKey = `${MENTION_TOAST_CURSOR_STORAGE_PREFIX}:${userKey}`;

  const resolvedChatbotIds = useMemo(
    () => resolveScopedChatbotIds(scopeChatbotIds),
    [scopeChatbotIds],
  );

  const openNotificationTarget = useCallback(
    (target: MentionNotificationTarget | null | undefined) => {
      const params = new URLSearchParams();
      params.set("fejlstatus", "unread_mentions");
      if (target?.conversationId) {
        params.set("conversationId", String(target.conversationId));
      }
      if (target?.commentId) {
        params.set("commentId", String(target.commentId));
      }
      navigateToReportComment(history, `?${params.toString()}`);
    },
    [history],
  );

  const showMentionToast = useCallback(
    (target: MentionNotificationTarget) => {
      showActionToast({
        id: `di-mention-toast-${target.commentId ?? target.conversationId}`,
        title: t("mentionToastTitle"),
        description: t("mentionToastBody"),
        actionLabel: t("mentionToastOpen"),
        onAction: () => openNotificationTarget(target),
      });
    },
    [openNotificationTarget, t],
  );

  // Shares the nav badge's unread-counts query: one request per poll serves
  // both, and a hidden tab catches up on focus instead of polling.
  const unreadCountsQuery = useConversationUnreadCounts({
    chatbotIds: resolvedChatbotIds,
    enabled: isAuthenticated && hasOrganizationContext && commentNotificationsReadEnabled(),
    poll: true,
  });
  const unreadCountsDetails = unreadCountsQuery.data?.details;

  useEffect(() => {
    if (!unreadCountsDetails) {
      return;
    }
    const counts = normalizeUnreadCounts(unreadCountsDetails);
    const decision = resolveMentionToast(
      readMentionCursor(cursorStorageKey),
      counts.latestMentionNotification,
    );

    if (decision.shouldShow && decision.nextLastSeenCommentId != null) {
      // Persist before showing so a reload mid-toast never repeats it.
      writeMentionCursor(cursorStorageKey, decision.nextLastSeenCommentId);
      if (counts.latestMentionNotification) {
        showMentionToast(counts.latestMentionNotification);
      }
    }
  }, [cursorStorageKey, showMentionToast, unreadCountsDetails]);

  return null;
}
